import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { bool, func, number, shape, string } from "prop-types";
import { connect } from "react-redux";
import { compose } from "redux";
import { push } from "react-router-redux";
import { Redirect } from "react-router-dom";
import Yup from "yup";

import { CredinConfirmation, Spinner } from "@ui-kit/components/index.es";

import { resizableRoute } from "pages/_components/Resizable";

import { selectors as accountsSelectors } from "reducers/accounts";
import { actions as debinActions, selectors as debinSelectors, types as debinTypes } from "reducers/debin";
import { actions as formActions, selectors as formSelectors } from "reducers/form";
import { selectors as sessionSelectors } from "reducers/session";
import { actions as tokenActions, selectors as tokenSelectors } from "reducers/token";

import * as config from "util/config";
import { calculateTotalAmountCbuList } from "util/credin";
import * as i18n from "util/i18n";
import isTokenActive from "util/token";
import { useEmbeddedV3ShellLayoutSync, isCredinV3Enabled } from "util/v3ShellLayoutSync";
import {
    getCredinTicketPath,
    getRequestCredinPath,
    REQUEST_CREDIN_GENERATE_LEGACY_PATH,
    REQUEST_CREDIN_V3_PATH,
} from "util/v3UxConfig";

import {
    mapCredinConfirmationRecipients,
    mapCredinConfirmationSellerSummary,
    mapCredinConfirmationTotals,
} from "v3/pages/transfers/generateCredin";

/**
 * Scopes for v3 `ToastNotification`. Must intersect saga notification scopes:
 * - success / pending: `credin`
 * - confirm errors: `debin`, `confirmDebin`
 * - pending transaction signature errors: `signature`
 * - pending transaction cancellation errors: `cancelTransaction`, `form`
 */
const CREDIN_SCOPES = ["credin", "debin", "confirmDebin", "signature", "cancelTransaction", "form"];
const FORM_ID = "requestCredin.summary";

/**
 * V3 Credin confirmation screen. Renders the UI-kit `CredinConfirmation` and delegates OTP submit
 * to the existing CREDIN creation or pending-transaction signature sagas.
 */
function GenerateCredinV3Page(props) {
    const {
        dispatch,
        isDesktop,
        accounts,
        seller,
        recipients,
        cbuList,
        id,
        control,
        fetching,
        tokenNumber,
        fetchingToken,
        embeddedInV3Shell,
        summary,
        signTransactionData,
        cancelTransactionData,
        formFetching,
        userId,
    } = props;

    const isSignFlow = summary?.action === "sign" && signTransactionData?.idTransaction === summary?.idTransaction;
    const isCancelFlow =
        summary?.action === "cancel" && cancelTransactionData?.idTransaction === summary?.idTransaction;
    const enteredPendingActionRef = useRef(false);
    if (isSignFlow || isCancelFlow) {
        enteredPendingActionRef.current = true;
    }
    // Keep reading `summary` after cancel/sign success: redirectCancelled reloads it without `action`.
    const isPendingActionFlow = isSignFlow || isCancelFlow || enteredPendingActionRef.current;
    const isMobileWithToken = !isDesktop && isTokenActive();
    const confirmationSeller = isPendingActionFlow ? summary : seller;
    const confirmationRecipients = useMemo(() => {
        const source = isPendingActionFlow ? summary?.recipients : recipients;
        if (!source) {
            return [];
        }
        return Array.isArray(source) ? source : [source];
    }, [isPendingActionFlow, recipients, summary?.recipients]);
    const confirmationControl = isPendingActionFlow ? summary?.control : control;

    useEffect(() => {
        if (!confirmationSeller || confirmationRecipients.length === 0) {
            dispatch(push(isPendingActionFlow ? getCredinTicketPath() : getRequestCredinPath()));
        }
    }, [confirmationRecipients, confirmationSeller, dispatch, isPendingActionFlow]);

    useEffect(() => {
        if (isMobileWithToken) {
            dispatch(tokenActions.softTokenGenerationRequest());
        }
    }, [dispatch, isMobileWithToken]);

    useEffect(() => {
        if (isPendingActionFlow) {
            return undefined;
        }
        const onPopState = () => {
            const nextPathname = window.location.pathname;
            if (nextPathname === getRequestCredinPath() || nextPathname === REQUEST_CREDIN_V3_PATH) {
                dispatch({ type: debinTypes.SET_IS_BACK_FROM_SUMMARY });
            }
        };
        window.addEventListener("popstate", onPopState);
        return () => window.removeEventListener("popstate", onPopState);
    }, [dispatch, isPendingActionFlow]);

    useEmbeddedV3ShellLayoutSync(dispatch, {
        embeddedInV3Shell,
        isDesktop,
        titleKey: "requestCredin.title",
        goBackPath: isPendingActionFlow ? getCredinTicketPath() : getRequestCredinPath(),
        scopes: CREDIN_SCOPES,
    });

    const sellerSummary = useMemo(() => mapCredinConfirmationSellerSummary(confirmationSeller, accounts), [
        accounts,
        confirmationSeller,
    ]);
    const recipientCards = useMemo(() => mapCredinConfirmationRecipients(confirmationRecipients), [
        confirmationRecipients,
    ]);
    const confirmationTotals = useMemo(() => mapCredinConfirmationTotals(confirmationRecipients), [
        confirmationRecipients,
    ]);

    const showDetailedTotals = useMemo(
        () => config.getBoolean("frontend.esquemaFirmas.validation.credinCVU.enable", false),
        [],
    );

    const titleHintText = useMemo(() => {
        if (!confirmationControl) {
            return undefined;
        }
        return config.get("frontend.mobileSignatureControl.text", i18n.get("credin.confirmation.mobileApprovalHint"));
    }, [confirmationControl]);

    const handleBack = useCallback(() => {
        if (isPendingActionFlow) {
            dispatch(push(getCredinTicketPath()));
            return;
        }
        dispatch(debinActions.setIsBackFromSummary(getRequestCredinPath()));
    }, [dispatch, isPendingActionFlow]);

    const getToken = useCallback(async () => {
        if (isMobileWithToken && tokenNumber) {
            return { token: tokenNumber };
        }
        return { token: null };
    }, [isMobileWithToken, tokenNumber]);

    const handleConfirm = useCallback(
        (values, formikBag) => {
            const finalOtp = isMobileWithToken && tokenNumber ? tokenNumber : values.otp;
            if (isSignFlow) {
                dispatch(
                    formActions.signTransaction({
                        credentials: { otp: finalOtp },
                        formikBag,
                        idTransaction: signTransactionData.idTransaction,
                        idForm: signTransactionData.idForm,
                        idActivity: signTransactionData.idActivity,
                        userId,
                        pendingDispatch: signTransactionData.pendingDispatch,
                        useTransactionData: signTransactionData.useTransactionData,
                    }),
                );
                return;
            }
            if (isCancelFlow) {
                dispatch(
                    formActions.cancelTransaction({
                        credentials: { otp: finalOtp },
                        formikBag,
                        idTransaction: cancelTransactionData.idTransaction,
                        userId,
                        rejectedTransactions: false,
                        force: cancelTransactionData.force,
                    }),
                );
                return;
            }
            const amount = calculateTotalAmountCbuList(cbuList);
            dispatch(
                debinActions.generateCredin(finalOtp, { ...seller, amount }, recipients, id, {
                    ...formikBag,
                    props: { cbuList },
                }),
            );
        },
        [
            cancelTransactionData,
            cbuList,
            dispatch,
            id,
            isCancelFlow,
            isMobileWithToken,
            isSignFlow,
            recipients,
            seller,
            signTransactionData,
            tokenNumber,
            userId,
        ],
    );

    const getCardLabel = useCallback((labelId) => i18n.get(`credin.confirmation.${labelId}`), []);

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                otp: Yup.string().required(i18n.get(`${FORM_ID}.otp.error.required`)),
            }),
        [],
    );

    if (!isCredinV3Enabled()) {
        return <Redirect to={REQUEST_CREDIN_GENERATE_LEGACY_PATH} />;
    }

    const isTokenPending = isMobileWithToken && (fetchingToken || !tokenNumber);
    if (isTokenPending) {
        return <Spinner size="large" />;
    }

    return (
        <CredinConfirmation
            goBack={handleBack}
            pageTitle={i18n.get("requestCredin.title")}
            titleHintText={titleHintText}
            headText={isCancelFlow ? i18n.get("cancel.check.transaction") : undefined}
            loading={fetching && confirmationRecipients.length === 0}
            sellerSummary={sellerSummary}
            totals={confirmationTotals}
            showDetailedTotals={showDetailedTotals}
            recipients={recipientCards}
            getFormLabel={getCardLabel}
            confirmationProps={{
                loading: (isPendingActionFlow ? formFetching : fetching) || fetchingToken,
                isInactive: (isPendingActionFlow ? formFetching : fetching) || fetchingToken,
                confirmationText: i18n.get(
                    isMobileWithToken ? "confirmation.withoutotp.text" : "credin.confirmation.confirmationText",
                ),
                handleConfirm,
                getToken,
                validationConfirm: validationSchema,
            }}
        />
    );
}

GenerateCredinV3Page.propTypes = {
    dispatch: func.isRequired,
    isDesktop: bool.isRequired,
    accounts: shape({}).isRequired,
    seller: shape({}),
    recipients: shape({}),
    cbuList: shape({}),
    id: number,
    control: bool,
    fetching: bool.isRequired,
    tokenNumber: string,
    fetchingToken: bool.isRequired,
    embeddedInV3Shell: bool,
    summary: shape({}),
    signTransactionData: shape({}),
    cancelTransactionData: shape({}),
    formFetching: bool,
    userId: string,
};

GenerateCredinV3Page.defaultProps = {
    seller: null,
    recipients: [],
    cbuList: [],
    id: null,
    control: false,
    tokenNumber: null,
    embeddedInV3Shell: false,
    summary: null,
    signTransactionData: null,
    cancelTransactionData: null,
    formFetching: false,
    userId: "",
};

const mapStateToProps = (state) => ({
    accounts: accountsSelectors.getAccounts(state),
    seller: debinSelectors.getDebinSeller(state),
    recipients: debinSelectors.getRecipients(state),
    cbuList: debinSelectors.getListCbu(state),
    id: debinSelectors.getId(state),
    control: debinSelectors.getControl(state),
    fetching: debinSelectors.getFetching(state),
    tokenNumber: tokenSelectors.getTokenNumber(state),
    fetchingToken: tokenSelectors.getFetchingToken(state),
    summary: debinSelectors.getSummary(state),
    signTransactionData: formSelectors.getSignTransactionData(state),
    cancelTransactionData: formSelectors.getCancelTransactionData(state),
    formFetching: formSelectors.getFetching(state),
    userId: sessionSelectors.getUser(state)?.userId,
});

export default compose(connect(mapStateToProps), resizableRoute)(GenerateCredinV3Page);
